import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { qAppConfig, type AppConfigRow } from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import {
  usePowerSyncDb,
  usePowerSyncQuery,
  getLatestSyncFetchDiagnosis,
  onSyncFetchDiagnosis,
  type SyncFetchDiagnosis,
} from '../../adapters/powersync';
import { useInitialSyncMilestones } from '../../sync/useInitialSyncMilestones';
import { getUploadQueueStats, useUploadQueueStats } from '../../sync/useUploadQueueStats';
import { useSyncStatus } from '../../sync/useSyncStatus';
import { retrySync, forceResync } from '../../sync/syncActions';
import { describeSyncHealthState, recordRetryLog } from '../../sync/syncHealth';
import { createNeedHelpCase } from '../../sync/syncNeedHelp';

// ─── Types ───

interface SyncInfo {
  connected: boolean;
  connecting: boolean;
  lastSyncedAt: string | null;
  hasSynced: boolean;
  downloading: boolean;
  uploading: boolean;
  downloadError: string | null;
  uploadError: string | null;
}

interface SyncedTableRow {
  name: string;
  rows: number;
}

// ─── Styles ───

const pageStyle: CSSProperties = {
  padding: 'var(--sp-8, 32px)',
  maxWidth: 640,
  height: '100%',
  overflowY: 'auto',
};

const sectionStyle: CSSProperties = {
  padding: 'var(--sp-5, 20px)',
  background: 'var(--bg-surface, #fff)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  borderRadius: 'var(--radius-lg, 14px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3, 12px)',
};

const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  color: 'var(--text, #0f172a)',
  margin: 0,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 'var(--font-size-sm, 14px)',
};

const labelStyle: CSSProperties = {
  color: 'var(--text-muted, #64748b)',
};

const valueStyle: CSSProperties = {
  fontWeight: 'var(--font-weight-medium, 500)',
  color: 'var(--text, #0f172a)',
  fontVariantNumeric: 'tabular-nums',
};

// ─── Status Badge ───

function StatusBadge({ connected }: { connected: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-full, 9999px)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        background: connected ? '#f0fdf4' : '#fef2f2',
        color: connected ? '#16a34a' : '#dc2626',
        border: `1px solid ${connected ? '#bbf7d0' : '#fecaca'}`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: connected ? '#16a34a' : '#dc2626',
        }}
      />
      {connected ? 'Connected' : 'Disconnected'}
    </span>
  );
}

// ─── Health Badge (derived state, PRD §6.2) ───

const HEALTH_TONE: Record<string, { bg: string; fg: string; border: string }> = {
  ok: { bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0' },
  neutral: { bg: '#f1f5f9', fg: '#475569', border: '#e2e8f0' },
  warn: { bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
  error: { bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
};

function HealthBadge({ state }: { state: ReturnType<typeof useSyncStatus>['state'] }) {
  const { label, tone } = describeSyncHealthState(state);
  const c = HEALTH_TONE[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-full, 9999px)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 'var(--font-weight-medium, 500)',
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.fg }} />
      {label}
    </span>
  );
}

// ─── Page Component ───

interface SupportState {
  submitting: boolean;
  caseId: string | null;
  error: string | null;
}

export function SyncStatusPage() {
  const { db, connector, isConnected } = usePowerSyncDb();
  const { user, getIdToken } = useAuth();
  const health = useSyncStatus();
  const milestones = useInitialSyncMilestones();
  const uploadQueue = useUploadQueueStats();
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());

  const [support, setSupport] = useState<SupportState>({
    submitting: false,
    caseId: null,
    error: null,
  });

  const [syncInfo, setSyncInfo] = useState<SyncInfo>({
    connected: false,
    connecting: false,
    lastSyncedAt: null,
    hasSynced: false,
    downloading: false,
    uploading: false,
    downloadError: null,
    uploadError: null,
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);
  const [syncedTables, setSyncedTables] = useState<SyncedTableRow[]>([]);
  const [tablesError, setTablesError] = useState<string | null>(null);
  const [tableFilter, setTableFilter] = useState('');
  // Refined cause of a "Failed to fetch" — set by the endpoint probe in the
  // PowerSync provider (CORS vs unreachable vs auth). Lets support read the
  // real reason without DevTools.
  const [fetchDiagnosis, setFetchDiagnosis] = useState<SyncFetchDiagnosis | null>(
    getLatestSyncFetchDiagnosis,
  );
  useEffect(() => onSyncFetchDiagnosis(setFetchDiagnosis), []);

  // Poll sync status
  useEffect(() => {
    if (!db) return;

    function update() {
      const s = db!.currentStatus;
      setSyncInfo({
        connected: s?.connected ?? false,
        connecting: s?.connecting ?? false,
        lastSyncedAt: s?.lastSyncedAt ? new Date(s.lastSyncedAt).toLocaleString() : null,
        hasSynced: s?.hasSynced ?? false,
        downloading: s?.dataFlowStatus?.downloading ?? false,
        uploading: s?.dataFlowStatus?.uploading ?? false,
        downloadError:
          s?.connected && s?.hasSynced ? null : (s?.dataFlowStatus?.downloadError?.message ?? null),
        uploadError:
          s?.connected && s?.hasSynced ? null : (s?.dataFlowStatus?.uploadError?.message ?? null),
      });
    }

    update();
    const interval = setInterval(update, 2_000);
    return () => clearInterval(interval);
  }, [db]);

  // Poll which tables PowerSync has provisioned locally + their row counts.
  // PowerSync stores synced data under `ps_data__<table>`. We strip the
  // prefix for display. Refreshed less aggressively than connection
  // status because COUNT(*) per-table is heavier.
  useEffect(() => {
    if (!db) return;
    let cancelled = false;

    async function refresh() {
      try {
        const psTables = await db!.getAll<{ name: string }>(
          `SELECT name FROM sqlite_master
           WHERE type='table' AND name LIKE 'ps_data__%'
           ORDER BY name`,
        );
        const counts: SyncedTableRow[] = [];
        for (const { name: rawName } of psTables) {
          // rawName comes from sqlite_master, so direct interpolation is
          // safe (no user input). SQLite cannot parameterize table names.
          const rows = await db!.getAll<{ n: number }>(`SELECT COUNT(*) AS n FROM "${rawName}"`);
          if (cancelled) return;
          counts.push({
            name: rawName.replace(/^ps_data__/, ''),
            rows: rows[0]?.n ?? 0,
          });
        }
        if (cancelled) return;
        setSyncedTables(counts);
        setTablesError(null);
      } catch (err) {
        if (cancelled) return;
        setTablesError(err instanceof Error ? err.message : String(err));
      }
    }

    void refresh();
    const interval = setInterval(refresh, 5_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [db]);

  const handleRetry = useCallback(async () => {
    if (!db || !connector) return;
    setIsRetrying(true);
    const pendingBefore = uploadQueue.count;
    recordRetryLog({
      timestamp: new Date().toISOString(),
      action: 'retry',
      pendingBefore,
      outcome: 'started',
    });
    try {
      await retrySync(db, connector);
      // Give the queue a moment to drain before reading the result.
      await new Promise((r) => setTimeout(r, 3_000));
      const after = await getUploadQueueStats(db);
      recordRetryLog({
        timestamp: new Date().toISOString(),
        action: 'retry',
        pendingBefore,
        pendingAfter: after.count,
        outcome: 'success',
        detail: after.count > 0 ? 'queue still has pending operations' : 'queue drained',
      });
    } catch (err) {
      console.error('[SyncStatus] Retry failed:', err);
      recordRetryLog({
        timestamp: new Date().toISOString(),
        action: 'retry',
        pendingBefore,
        outcome: 'error',
        detail: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsRetrying(false);
    }
  }, [db, connector, uploadQueue.count]);

  const handleAskSupport = useCallback(async () => {
    if (!db) return;
    setSupport({ submitting: true, caseId: null, error: null });
    try {
      const result = await createNeedHelpCase({
        db,
        health,
        user: { uid: user?.uid ?? null, email: user?.email ?? null },
        getIdToken: (force) => getIdToken(force),
        clinicName: import.meta.env.VITE_ORG_NAME,
      });
      setSupport({ submitting: false, caseId: result.caseId, error: null });
    } catch (err) {
      console.error('[SyncStatus] Ask Support failed:', err);
      setSupport({
        submitting: false,
        caseId: null,
        error: err instanceof Error ? err.message : 'Failed to send support request.',
      });
    }
  }, [db, health, user, getIdToken]);

  const handleForceResync = useCallback(async () => {
    if (!db) return;
    if (uploadQueue.hasPending && !confirmForce) {
      setConfirmForce(true);
      return;
    }
    setIsRetrying(true);
    recordRetryLog({
      timestamp: new Date().toISOString(),
      action: 'force-resync',
      pendingBefore: uploadQueue.count,
      outcome: 'started',
      detail: uploadQueue.hasPending ? 'force resync with pending uploads' : 'force resync',
    });
    try {
      await forceResync(db);
    } catch (err) {
      console.error('[SyncStatus] Force resync failed:', err);
      setIsRetrying(false);
    }
  }, [db, uploadQueue.hasPending, uploadQueue.count, confirmForce]);

  return (
    <div style={pageStyle}>
      <h1
        style={{
          fontSize: 'var(--font-size-xl, 20px)',
          fontWeight: 'var(--font-weight-semi, 600)',
          color: 'var(--text, #0f172a)',
          margin: '0 0 var(--sp-6, 24px)',
        }}
      >
        Sync Status
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5, 20px)' }}>
        {/* Connection Status */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Connection</h2>
          <div style={rowStyle}>
            <span style={labelStyle}>Sync health</span>
            <HealthBadge state={health.state} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Connection</span>
            <StatusBadge connected={isConnected} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Last synced</span>
            <span style={valueStyle}>{syncInfo.lastSyncedAt ?? 'Never'}</span>
          </div>

          {(health.state === 'stale' || health.state === 'stuck' || health.state === 'offline') && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: health.state === 'stuck' ? '#991b1b' : '#92400e',
                background: health.state === 'stuck' ? '#fef2f2' : '#fffbeb',
                border: `1px solid ${health.state === 'stuck' ? '#fecaca' : '#fde68a'}`,
                padding: 'var(--sp-3, 12px)',
                borderRadius: 'var(--radius-md, 10px)',
                lineHeight: 1.5,
              }}
            >
              {health.state === 'offline'
                ? 'This device appears to be offline. Changes are saved locally and will upload when the connection returns.'
                : health.state === 'stuck'
                  ? `Some changes on this device have not uploaded${health.pendingCount > 0 ? ` (${health.pendingCount} pending)` : ''}. You can retry syncing, or send a support request so our team can safely inspect the pending changes before any reset.`
                  : `Sync looks stale${health.pendingCount > 0 ? ` — ${health.pendingCount} change(s) are still waiting to upload` : ''}. Try Retry Sync below.`}
            </div>
          )}
          <div style={rowStyle}>
            <span style={labelStyle}>Downloading</span>
            <span style={valueStyle}>{syncInfo.downloading ? 'Yes' : 'No'}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Uploading</span>
            <span style={valueStyle}>{syncInfo.uploading ? 'Yes' : 'No'}</span>
          </div>
          {syncInfo.downloadError && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#dc2626',
                background: '#fef2f2',
                padding: 'var(--sp-2, 8px)',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              Download error: {syncInfo.downloadError}
            </div>
          )}
          {syncInfo.uploadError && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#dc2626',
                background: '#fef2f2',
                padding: 'var(--sp-2, 8px)',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              Upload error: {syncInfo.uploadError}
            </div>
          )}
          {health.lastErrorMessage && (
            <div style={rowStyle}>
              <span style={labelStyle}>Last sync error</span>
              <span
                style={{
                  ...valueStyle,
                  maxWidth: 320,
                  textAlign: 'right',
                  color: '#b45309',
                  fontWeight: 400,
                }}
                title={health.lastErrorMessage}
              >
                {health.lastErrorMessage.length > 80
                  ? `${health.lastErrorMessage.slice(0, 80)}…`
                  : health.lastErrorMessage}
              </span>
            </div>
          )}
          {/* Refined cause from the endpoint probe — only meaningful while a
          fetch failure is active. */}
          {health.lastErrorMessage && fetchDiagnosis && /fetch/i.test(health.lastErrorMessage) && (
            <div style={rowStyle}>
              <span style={labelStyle}>Likely cause</span>
              <span
                style={{
                  ...valueStyle,
                  maxWidth: 360,
                  textAlign: 'right',
                  color: '#b45309',
                  fontWeight: 400,
                }}
                title={`${fetchDiagnosis.verdict}\nendpoint: ${fetchDiagnosis.probedUrl}\nreachable: ${fetchDiagnosis.reachable} · corsBlocked: ${fetchDiagnosis.corsBlocked} · online: ${fetchDiagnosis.online}`}
              >
                {fetchDiagnosis.verdict}
              </span>
            </div>
          )}
        </div>

        {/* Upload Queue */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Upload Queue</h2>
          <div style={rowStyle}>
            <span style={labelStyle}>Pending operations</span>
            <span style={valueStyle}>{uploadQueue.count}</span>
          </div>
        </div>

        {/* Milestones */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Data Milestones</h2>
          {milestones.milestones.map((m) => (
            <div key={m.key} style={rowStyle}>
              <span style={labelStyle}>
                {m.done ? '\u2705' : '\u23F3'} {m.label}
              </span>
              <span style={valueStyle}>{m.count} rows</span>
            </div>
          ))}
        </div>

        {/* Tables Synced */}
        <div style={sectionStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2 style={sectionTitle}>
              Tables Synced{' '}
              <span style={{ color: 'var(--text-muted, #64748b)', fontWeight: 400 }}>
                ({syncedTables.length} {syncedTables.length === 1 ? 'table' : 'tables'},{' '}
                {syncedTables.reduce((sum, t) => sum + t.rows, 0).toLocaleString()} rows)
              </span>
            </h2>
          </div>
          <input
            type="text"
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            placeholder="Filter tables\u2026"
            style={{
              height: 32,
              padding: '0 var(--sp-3, 12px)',
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid var(--border, rgba(15,23,42,0.12))',
              background: 'var(--bg-surface, #fff)',
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'var(--text, #0f172a)',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {tablesError && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: '#dc2626',
                background: '#fef2f2',
                padding: 'var(--sp-2, 8px)',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              {tablesError}
            </div>
          )}
          <div
            style={{
              maxHeight: 360,
              overflowY: 'auto',
              border: '1px solid var(--border, rgba(15,23,42,0.08))',
              borderRadius: 'var(--radius-md, 10px)',
            }}
          >
            {syncedTables.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-4, 16px)',
                  fontSize: 'var(--font-size-sm, 14px)',
                  color: 'var(--text-muted, #64748b)',
                  textAlign: 'center',
                }}
              >
                {tablesError ? 'Unable to read local tables.' : 'No PowerSync tables yet.'}
              </div>
            ) : (
              syncedTables
                .filter((t) =>
                  tableFilter.trim()
                    ? t.name.toLowerCase().includes(tableFilter.toLowerCase().trim())
                    : true,
                )
                .map((t) => (
                  <div
                    key={t.name}
                    style={{
                      ...rowStyle,
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--border, rgba(15,23,42,0.06))',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        color: 'var(--text, #0f172a)',
                        fontSize: 'var(--font-size-xs, 12px)',
                      }}
                    >
                      {t.name}
                    </span>
                    <span
                      style={{
                        ...valueStyle,
                        color: t.rows > 0 ? 'var(--text, #0f172a)' : 'var(--text-muted, #94a3b8)',
                      }}
                    >
                      {t.rows.toLocaleString()}
                    </span>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* App Config */}
        <div style={sectionStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2 style={sectionTitle}>
              App Config{' '}
              <span style={{ color: 'var(--text-muted, #64748b)', fontWeight: 400 }}>
                ({appConfigRows.length} {appConfigRows.length === 1 ? 'row' : 'rows'})
              </span>
            </h2>
            <button
              type="button"
              onClick={() => {
                const text = JSON.stringify(appConfigRows, null, 2);
                void navigator.clipboard.writeText(text);
              }}
              disabled={appConfigRows.length === 0}
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                padding: '4px 10px',
                borderRadius: 'var(--radius-sm, 6px)',
                border: '1px solid var(--border, rgba(15,23,42,0.12))',
                background: 'var(--bg-surface, #fff)',
                color: 'var(--text, #0f172a)',
                cursor: appConfigRows.length === 0 ? 'not-allowed' : 'pointer',
                opacity: appConfigRows.length === 0 ? 0.5 : 1,
              }}
            >
              Copy JSON
            </button>
          </div>
          {appConfigRows.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-4, 16px)',
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--text-muted, #64748b)',
                textAlign: 'center',
                border: '1px solid var(--border, rgba(15,23,42,0.08))',
                borderRadius: 'var(--radius-md, 10px)',
              }}
            >
              No app_config rows synced yet.
            </div>
          ) : (
            <pre
              style={{
                margin: 0,
                padding: 'var(--sp-3, 12px)',
                background: '#0f172a',
                color: '#e2e8f0',
                borderRadius: 'var(--radius-md, 10px)',
                fontSize: 11,
                lineHeight: 1.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                maxHeight: 360,
                overflow: 'auto',
                whiteSpace: 'pre',
              }}
            >
              {JSON.stringify(appConfigRows, null, 2)}
            </pre>
          )}
        </div>

        {/* Actions — recommended order: Retry → Ask Support → Force Resync (PRD §5) */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Actions</h2>

          {/* Step 1 — Retry */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              style={{
                padding: '10px 20px',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid var(--border, rgba(15,23,42,0.12))',
                background: 'var(--bg-surface, #fff)',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: 'var(--text, #0f172a)',
                cursor: isRetrying ? 'not-allowed' : 'pointer',
                opacity: isRetrying ? 0.5 : 1,
              }}
            >
              {isRetrying ? 'Retrying…' : 'Retry Sync'}
            </button>
            <span style={{ ...labelStyle, fontSize: 'var(--font-size-xs, 12px)' }}>
              Reconnect and try to upload pending changes.
            </span>
          </div>

          {/* Step 2 — Ask Support for Help */}
          <div
            style={{
              borderTop: '1px solid var(--border, rgba(15,23,42,0.08))',
              paddingTop: 'var(--sp-4, 16px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3, 12px)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3, 12px)' }}>
              <button
                onClick={handleAskSupport}
                disabled={support.submitting || !db}
                style={{
                  padding: '10px 20px',
                  borderRadius: 'var(--radius-md, 10px)',
                  border: 'none',
                  background: 'var(--primary, #2563eb)',
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontWeight: 'var(--font-weight-semi, 600)',
                  color: '#fff',
                  cursor: support.submitting ? 'not-allowed' : 'pointer',
                  opacity: support.submitting ? 0.6 : 1,
                }}
              >
                {support.submitting ? 'Sending…' : 'Ask Support for Help'}
              </button>
              <span style={{ ...labelStyle, fontSize: 'var(--font-size-xs, 12px)' }}>
                Send a diagnostic bundle so support can inspect pending changes.
              </span>
            </div>

            {support.caseId && (
              <div
                style={{
                  padding: 'var(--sp-3, 12px)',
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: 'var(--radius-md, 10px)',
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: '#166534',
                  lineHeight: 1.5,
                }}
              >
                Support request sent. Case ID: <strong>{support.caseId}</strong>. Please keep this
                device open and avoid Force Resync unless support asks you to proceed.
              </div>
            )}
            {support.error && (
              <div
                style={{
                  padding: 'var(--sp-3, 12px)',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 'var(--radius-md, 10px)',
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: '#991b1b',
                }}
              >
                {support.error}
              </div>
            )}
          </div>

          {/* Step 3 — Force Resync (last resort) */}
          <div
            style={{
              borderTop: '1px solid var(--border, rgba(15,23,42,0.08))',
              paddingTop: 'var(--sp-4, 16px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3, 12px)',
            }}
          >
            {uploadQueue.hasPending && (
              <div
                style={{
                  padding: 'var(--sp-3, 12px)',
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: 'var(--radius-md, 10px)',
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: '#92400e',
                  lineHeight: 1.5,
                }}
              >
                This may remove changes saved only on this device
                {uploadQueue.count > 0 ? ` (${uploadQueue.count} pending)` : ''}. Send a support
                request first if you are unsure.
              </div>
            )}
            {confirmForce && uploadQueue.hasPending && (
              <div
                style={{
                  padding: 'var(--sp-3, 12px)',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 'var(--radius-md, 10px)',
                  fontSize: 'var(--font-size-xs, 12px)',
                  color: '#991b1b',
                }}
              >
                You have {uploadQueue.count} pending upload(s) that will be lost. Click Force Resync
                again to confirm.
              </div>
            )}
            <button
              onClick={handleForceResync}
              disabled={isRetrying}
              style={{
                alignSelf: 'flex-start',
                padding: '10px 20px',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid #fecaca',
                background: '#fef2f2',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                color: '#dc2626',
                cursor: isRetrying ? 'not-allowed' : 'pointer',
                opacity: isRetrying ? 0.5 : 1,
              }}
            >
              {isRetrying ? 'Clearing…' : 'Force Resync'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
